<template>
    <fieldset class='poolsdialog'>
        <legend>
            Curve pools
            <span class='tooltip' v-show='this.sumBalances > 0'>
                $
                <span class='tooltiptext'>
                    My total balances: ${{ this.sumBalances.toFixed(2) | formatNumber(0) }}
                </span>
            </span>
        </legend>

        <div class="table-controls" v-show="!displayMax">
            <input type="text" v-model='searchText' placeholder="Search" class="search-text" />
            <button @click="filterByPoolType('all')" :class="{ button: true, pressed: this.poolTypeFilter === 'all' }">
                All
            </button>
            <button @click="filterByPoolType('usd')" :class="{ button: true, pressed: this.poolTypeFilter === 'usd' }">
                USD
            </button>
            <button @click="filterByPoolType('btc')" :class="{ button: true, pressed: this.poolTypeFilter === 'btc' }">
                BTC
            </button>
            <button @click="filterByPoolType('eth')" :class="{ button: true, pressed: this.poolTypeFilter === 'eth' }">
                {{ config.nativeCurrency.symbol }}
            </button>
            <button @click="filterByPoolType('crypto')"
                :class="{ button: true, pressed: this.poolTypeFilter === 'crypto' }">
                Crypto
            </button>
            <button @click="filterByPoolType('others')"
                :class="{ button: true, pressed: this.poolTypeFilter === 'others' }">
                Others
            </button>
            <button @click="filterByPoolType('mine')"
                :class="{ button: true, pressed: this.poolTypeFilter === 'mine' }">
                My Dashboard
            </button>
            <input type="text" v-model='viewedAddress' placeholder="View Address" class="viewed-address"
                v-show="showPersonalStats" />
        </div>

        <div v-if="hasTvlDataAvailable" class="hide-low-tvl-pools">
            <input id='hideLowTvlPools' type='checkbox' value='hideLowTvlPools' v-model='hideLowTvlPools' />
            <label for='hideLowTvlPools'>Hide very small pools</label>
        </div>

        <div v-show="this.poolTypeFilter === 'crypto'" class="info-message gentle-message" style="margin-bottom:14px;">
            Crypto pools are part of Curve v2. Learn about crypto pools <a target="_blank" style="display:contents"
                href="https://resources.curve.finance/base-features/understanding-crypto-pools">by clicking here</a>
        </div>

        <sorted-table :values="getTableValues(getPools(), displayOnly, displayMax, this.claimableCRV)"
            sort="volumeSortingKey" dir="desc" ascIcon="<span class='sorting-icon'>▲</span>"
            descIcon="<span class='sorting-icon'>▼</span>" :class="{ showPersonalStats }">
            <thead class="lh">
                <tr>
                    <td scope="col">
                        <sort-link name="name">Pool</sort-link>
                    </td>
                    <td scope="col">
                        <sort-link name="dailyAPY">Base vAPY
                            <span class='tooltip'>?
                                <span class='tooltiptext long'>
                                    Variable APY based on today's trading activity
                                </span>
                            </span>
                        </sort-link><br />
                        <sort-link name="crvAPYsSortingKey">Rewards tAPR
                            <span class='tooltip'>?
                                <span class='tooltiptext long'>
                                    Token APR based on current prices of tokens and reward rates
                                </span>
                            </span>
                        </sort-link>
                    </td>
                    <td class="volume" scope="col">
                        <sort-link name="volumeSortingKey">Volume</sort-link>
                    </td>
                    <td class="tvl" scope="col" v-if="hasTvlDataAvailable">
                        <sort-link name="tvlSortingKey">TVL</sort-link>
                    </td>
                    <td class="balance" v-show="!showPersonalStats">
                        <span class="mobile-show">Balance</span>
                    </td>
                    <td scope="col" v-show="showPersonalStats">
                        <sort-link name="balances">Balance</sort-link>
                    </td>
                    <td scope="col" v-show="showPersonalStats">
                        <sort-link name="usdPerDay">USD Profits</sort-link><br />
                        <sort-link name="crvPerDay">CRV Profits</sort-link>
                    </td>
                    <td scope="col" v-show="showPersonalStats">
                        <sort-link name="claimableCRV">Claimable Tokens</sort-link>
                    </td>
                </tr>
            </thead>
            <template #body="{ values }">
                <component :is="pool.isFactoryOnlyPool ? 'FactoryV2Pool' : 'Pool'" v-for="pool in values" :key='pool.id'
                    :pool='pool' :dailyAPY='pool.dailyAPY' :weeklyAPY='pool.weeklyAPY' :monthlyAPY='pool.monthlyAPY'
                    :totalAPY='pool.totalAPY' :crvAPYs='pool.crvAPYs' :volumes='pool.volumes' :volume='pool.volume'
                    :usdTotal='pool.usdTotal' :balances='pool.balances' :usdPerDay='pool.usdPerDay'
                    :crvPerDay='pool.crvPerDay' :apy='pool.apy' :claimableCRV='pool.claimableCRV'
                    :additionalRewards='pool.additionalRewards' :additionalRewardsPerDay='pool.additionalRewardsPerDay'
                    :claimableAdditionalRewards='pool.claimableAdditionalRewards' :showPersonalStats='showPersonalStats'
                    :CRVprice='CRVprice' />

                <tr v-if="values.length === 0 && !isLoadingData">
                    <td colspan="8" class="empty-td">
                        <span v-if="viewedAddress === ''">
                            No active pool to display, please enter an address
                        </span>
                        <span v-else>
                            No active pool for address <code>{{ viewedAddress }}</code>
                        </span>
                    </td>
                </tr>
            </template>
        </sorted-table>

        <div v-if="isLoadingData" class="loading line full-line" />

        <span v-show='displayOnly || displayMax' class="all-pools-link-container">
            <a href="/pools"><button>See All Pools</button></a>
            &nbsp; <router-link v-show="this.userAddress" :to="'/pools?see=' + this.userAddress"><button>My
                    Dashboard</button></router-link>
        </span>
    </fieldset>
</template>

<script>
import Vue from 'vue';
import config from '@/constants/config'
import getFactoryV2Pools from '@/utils/data/getFactoryV2Pools'
import getFactoryCryptoPools from '@/utils/data/getFactoryCryptoPools'
import getHiddenPools from '@/utils/data/getHiddenPools'
import getCRVAPY, { getFactoCRVAPY } from '@/utils/data/getCRVAPY'
import getAPY from '@/utils/data/getAPY'
import { SortedTable, SortLink } from 'vue-sorted-table';
import getPricesForAssets from '@/utils/data/getPricesForAssets'
import getCurveRewards from '@/utils/data/getCurveRewards'
import getSidechainAdditionalRewards from '@/utils/data/getSidechainAdditionalRewards'
import getHarmonyRewards from '@/utils/data/getHarmonyRewards'

import getClaimableCRV from '@/utils/data/getClaimableCRV'
import getClaimableAdditionalRewards from '@/utils/data/getClaimableAdditionalRewards'
import getBalances from '@/utils/data/getBalances'
import getFactoryOnlyPoolsBalances from '@/utils/data/getFactoryOnlyPoolsBalances'
import Pool from './Pool.vue';
import FactoryV2Pool from './FactoryV2Pool.vue';
import pools, { getPools, isFactoryPoolDataLoaded } from '@/constants/pools';
import { isFactoryAbiDataLoaded } from '@/constants/abis';
import { arrayToHashmap } from '@/utils/helpers';
import { sleepUntil } from '@/utils/Async';
import { getNormalizedEthereumAddress } from '@/utils/Calls';
import { contract } from '@/contract';

const initialBalancesValues = arrayToHashmap(pools.map(({ id }) => [id, -1]));
const initialVolumesValues = arrayToHashmap(pools.map(({ id }) => [id, [-1, -1]]));

const getViewAddressFromUrl = () => {
    const search = document.location.search.slice(1);
    if (search === '') return null;

    const queries = search.split('&');
    const relevantQuery = queries.find((query) => query.startsWith('see='));
    if (!relevantQuery) return null;

    const addressString = relevantQuery.replace('see=', '');
    return addressString;
};

export default {
    components: {
        FactoryV2Pool,
        Pool,
        SortedTable,
        SortLink,
    },

    props: {
        displayOnly: {
            type: Array,
        },
        displayMax: {
            type: String,
        },
        handleViewMyPools: {
            type: Function,
        },
        handleUpdateResolvedAddress: {
            type: Function,
        },
    },

    data: () => ({
        config,
        isLoadingData: false,
        hideLowTvlPools: (config.blockchainId !== 'kava' && config.blockchainId !== 'celo'), // true by default, except for kava & celo where there are only facto pools
        pools,
        getPools,
        hiddenPoolIds: [],
        factoV2Pools: [],
        factoCryptoPools: [],
        balances: initialBalancesValues,
        sumBalances: 0,
        volumes: initialVolumesValues,
        daily_apy: null,
        weekly_apy: null,
        monthly_apy: null,
        apy: null,
        CRVprice: null,
        CRVAPYs: {},
        CRVboosts: {},
        boosts: {},
        userAddress: null,
        claimableCRV: null,
        claimableAdditionalRewards: null,
        additionalRewards: null,
        searchText: '',
        viewedAddress: '',
        resolvedAddress: '',
        poolTypeFilter: 'all',
    }),

    computed: {
        showPersonalStats() {
            return this.poolTypeFilter === 'mine';
        },
        hasTvlDataAvailable: () => !!config.mainPoolsEndpoint,
    },

    async created() {
        sleepUntil(() => (contract.web3 && contract.multicall)).then(() => {
            this.initialize();
        });
    },
    async mounted() {
        const {
            volumes,
            daily_apy,
            weekly_apy,
            monthly_apy,
            apy,
        } = await getAPY();

        this.volumes = volumes;
        this.daily_apy = daily_apy;
        this.weekly_apy = weekly_apy;
        this.monthly_apy = monthly_apy;
        this.apy = apy;

        if (contract.web3 && contract.multicall) this.initialize();
        if (contract.default_account) this.userAddress = contract.default_account
    },

    watch: {
        async showPersonalStats(showPersonalStats) {
            // Load claimable data only when displaying "My Pools"
            const needsClaimableData = showPersonalStats && (
                this.claimableCRV === null ||
                this.claimableAdditionalRewards === null
            );

            if (needsClaimableData) {
                await sleepUntil(() => (
                    isFactoryAbiDataLoaded() &&
                    isFactoryPoolDataLoaded() &&
                    this.resolvedAddress !== ''
                ));

                const [claimableCRV, claimableAdditionalRewards] = await Promise.all([
                    getClaimableCRV(this.resolvedAddress, config.factoryV2HasGauges),
                    getClaimableAdditionalRewards(this.resolvedAddress, config.factoryV2HasGauges),
                ]);

                const { augmentedClaimableCRV, augmentedClaimableAdditionalRewards } =
                    this.mergeCrvClaimableRewards({ claimableCRV, claimableAdditionalRewards });

                this.claimableCRV = augmentedClaimableCRV;
                this.claimableAdditionalRewards = augmentedClaimableAdditionalRewards;
            }
        },

        async viewedAddress(viewedAddress) {
            // Reset all user data to their initial values
            if (config.blockchainId === 'ethereum') {
                this.claimableCRV = null;
                this.claimableAdditionalRewards = null;
                this.CRVboosts = {};
                this.boosts = {};
            }
            this.balances = initialBalancesValues;
            this.sumBalances = 0;

            // Signal this reset to parent component
            if (this.handleUpdateResolvedAddress) this.handleUpdateResolvedAddress('', '');
            this.isLoadingData = true;

            // Check address validity
            const resolvedAddress = await getNormalizedEthereumAddress(viewedAddress);
            if (resolvedAddress === null) {
                this.isLoadingData = false;
                return;
            }

            // Signal this change to parent component
            if (this.handleUpdateResolvedAddress) this.handleUpdateResolvedAddress(viewedAddress, resolvedAddress);
            this.resolvedAddress = resolvedAddress;

            await sleepUntil(() => isFactoryPoolDataLoaded() && isFactoryAbiDataLoaded());

            // Fetch user data for new address
            const [claimableCRV, claimableAdditionalRewards] = await Promise.all([
                getClaimableCRV(resolvedAddress, config.factoryV2HasGauges),
                getClaimableAdditionalRewards(resolvedAddress, config.factoryV2HasGauges),
            ]);

            const { augmentedClaimableCRV, augmentedClaimableAdditionalRewards } =
                this.mergeCrvClaimableRewards({ claimableCRV, claimableAdditionalRewards });

            this.claimableCRV = { ...augmentedClaimableCRV };
            this.claimableAdditionalRewards = { ...augmentedClaimableAdditionalRewards };

            const { balances, sumBalances } = await getBalances(resolvedAddress);
            this.balances = { ...balances }; // Use new object instance for Vue to detect change
            this.sumBalances = sumBalances;

            this.isLoadingData = false;
        }
    },

    methods: {
        async initialize() {
            const viewAddressFromUrl = getViewAddressFromUrl();
            const resolvedViewAddressFromUrl = viewAddressFromUrl ?
                await getNormalizedEthereumAddress(viewAddressFromUrl) :
                viewAddressFromUrl;

            const initialViewAddress = resolvedViewAddressFromUrl ? viewAddressFromUrl : contract.default_account;
            const initialResolvedViewAddress = resolvedViewAddressFromUrl || contract.default_account;

            this.viewedAddress = initialViewAddress;
            this.resolvedAddress = initialResolvedViewAddress;
            if (this.handleUpdateResolvedAddress) this.handleUpdateResolvedAddress(this.viewedAddress, this.resolvedAddress);

            // If a valid address or ENS name was passed, view the dashboard automatically
            if (resolvedViewAddressFromUrl) {
                this.filterByPoolType('mine');
            }

            [this.factoV2Pools, this.factoCryptoPools] = await Promise.all([
                getFactoryV2Pools(),
                getFactoryCryptoPools(),
            ]);

            await sleepUntil(() => isFactoryPoolDataLoaded() && isFactoryAbiDataLoaded());

            if (config.blockchainId === 'ethereum') {
                const {
                    CRVprice,
                    CRVAPYs,
                    CRVboosts,
                    boosts,
                } = await getCRVAPY(this.resolvedAddress, config.factoryV2HasGauges);

                this.CRVprice = CRVprice;
                this.CRVAPYs = CRVAPYs;
                this.CRVboosts = CRVboosts;
                this.boosts = boosts;
            } else {
                const {
                    CRVprice,
                    CRVAPYs,
                    CRVboosts,
                    boosts,
                } = await getFactoCRVAPY();

                console.log({
                    CRVprice,
                    CRVAPYs,
                    CRVboosts,
                    boosts,
                })

                this.CRVprice = CRVprice;
                this.CRVAPYs = CRVAPYs;
                this.CRVboosts = CRVboosts;
                this.boosts = boosts;
            }

            this.hiddenPoolIds = await getHiddenPools();

            if (config.blockchainId === 'harmony') {
                const additionalRewards = await getHarmonyRewards();
                this.additionalRewards = additionalRewards;
            }

            if (
                config.blockchainId === 'xdai' ||
                config.blockchainId === 'polygon' ||
                config.blockchainId === 'kava' ||
                config.blockchainId === 'celo' ||
                config.blockchainId === 'avalanche' ||
                config.blockchainId === 'fantom' ||
                config.blockchainId === 'arbitrum' ||
                config.blockchainId === 'moonbeam'
            ) {
                const additionalRewards = await getSidechainAdditionalRewards();
                this.additionalRewards = additionalRewards;
            }


            const mainPoolsBalancesData = await getBalances(this.resolvedAddress);
            const factoPoolsBalancesData = await getFactoryOnlyPoolsBalances(this.resolvedAddress, [...this.factoV2Pools, ...this.factoCryptoPools]);
            this.balances = { ...mainPoolsBalancesData.balances, ...factoPoolsBalancesData.balances }; // Use new object instance for Vue to detect change
            this.sumBalances = mainPoolsBalancesData.sumBalances + factoPoolsBalancesData.sumBalances;
        },

        getTableValues(pools, displayOnly, displayMax, claimableCRV) {
            const augmentedPools = pools.filter(({ id, name, isBroken }) => (
                !this.hiddenPoolIds.includes(id) &&
                !isBroken &&
                (config.blockchainId !== 'kava' || name !== 'factory-v2-0') &&
                (config.blockchainId !== 'celo' || name !== 'factory-v2-0')
            )).map((pool) => {
                const index = pools.indexOf(pool);

                return {
                    ...pool,
                    dailyAPY: (
                        pool.isFactoryOnlyPool ?
                            pool.apy :
                            (this.daily_apy && Number(this.daily_apy[index]))
                    ),
                    weeklyAPY: (
                        pool.isFactoryOnlyPool ?
                            pool.apyWeekly :
                            (this.weekly_apy && Number(this.weekly_apy[index]))
                    ),
                    monthlyAPY: (
                        pool.isFactoryOnlyPool ?
                            undefined :
                            (this.monthly_apy && this.monthly_apy[index] ? Number(this.monthly_apy[index]) : undefined)
                    ),
                    totalAPY: (
                        pool.isFactoryOnlyPool ?
                            undefined :
                            (this.apy && this.apy?.[index] ? Number(this.apy[index]) : undefined)
                    ),
                    crvAPYs: this.CRVAPYs && this.CRVAPYs[pool.id],
                    crvAPYsSortingKey: (this.CRVAPYs && this.CRVAPYs[pool.id] && this.CRVAPYs[pool.id][0]) || 0,
                    claimableCRV: this.claimableCRV && this.claimableCRV[pool.id],
                    claimableAdditionalRewards: this.claimableAdditionalRewards && this.claimableAdditionalRewards[pool.id],
                    volumes: (
                        pool.isFactoryOnlyPool ?
                            [pool.volume] :
                            (this.volumes && this.volumes[pool.id])
                    ),
                    tvlSortingKey: pool.usdTotal || 0,
                    volumeSortingKey: (
                        pool.id === 'tricrypto2' ? 1e18 :
                            pool.isFactoryOnlyPool ? pool.volume :
                                (pool.id === 'atricrypto' || pool.id === 'atricrypto2' || pool.id === 'atricrypto3' || pool.id === 'tricrypto') ? (this.volumes && this.volumes[pool.id][1]) :
                                    (this.volumes && (this.volumes[pool.id][0] || this.volumes[pool.id][1]))
                    ),
                    balances: this.balances && this.balances[pool.id],
                    additionalRewards: this.poolAdditionalRewards(pool.id),
                };
            }).map((pool) => ({
                ...pool,
                usdPerDay: (pool.dailyAPY > 0 ? pool.dailyAPY : 0) / 100 / 365 * (pool.balances || 0),
                crvPerDay: (
                    (!pool.isPendingGaugeVoteToStartCrvRewards && !pool.hasNoGauge && pool.crvAPYs && pool.crvAPYs[3]) ?
                        pool.crvAPYs[3] / 100 / 365 * (pool.balances || 0) / this.CRVprice : null
                ),
                additionalRewardsPerDay: pool.additionalRewards.map(({ name, rewardsAmount, price }) => ({
                    name,
                    price,
                    rewardsAmount: rewardsAmount / 100 / 365 * (pool.balances || 0) / price,
                }))
            }));

            const tvlFilteredPools = (
                !this.hasTvlDataAvailable ? augmentedPools :
                    !this.hideLowTvlPools ? augmentedPools :
                        augmentedPools.filter(({ usdTotal }) => (
                            typeof usdTotal === 'undefined' ||
                            usdTotal > 10000
                        ))
            );

            // Default sorting is decided by the `sort` and `dir` attributes on the `sorted-table`
            // component, but is applied here too in order to reduce the set of pools to a consistent
            // set when `displayMax` is used. This way the column-based sorting is only run on the
            // `displayMax` first pools, and this set of pools never changes.
            const sortedPools = tvlFilteredPools.sort((poolA, poolB) => (
                poolA.volumeSortingKey > poolB.volumeSortingKey ? -1 :
                    poolA.volumeSortingKey < poolB.volumeSortingKey ? 1 : 0
            ));

            const displayedPools = displayOnly ?
                displayOnly.map((poolId) => sortedPools.find(({ id }) => id === poolId)) :
                sortedPools;

            const slicedPools = displayMax ? displayedPools.slice(0, displayMax) : displayedPools;

            const typeFilteredPools =
                this.poolTypeFilter === 'all' ? slicedPools :
                    this.poolTypeFilter === 'others' ? slicedPools.filter(({ referenceAsset }) => (
                        referenceAsset !== 'usd' &&
                        referenceAsset !== 'btc' &&
                        referenceAsset !== 'eth' &&
                        referenceAsset !== 'crypto'
                    )) :
                        (
                            this.poolTypeFilter === 'mine' ?
                                slicedPools.filter(({ balances, claimableCRV, claimableAdditionalRewards }) => (
                                    balances > 0 ||
                                    claimableCRV > 0 ||
                                    claimableAdditionalRewards?.some(({ amount }) => amount > 0)
                                )) :
                                slicedPools.filter(({ referenceAsset }) => referenceAsset === this.poolTypeFilter)
                        );

            const searchFilteredPools = this.searchText !== '' ?
                typeFilteredPools.filter((pool) => this.matchSearchText(pool, this.searchText)) :
                typeFilteredPools

            return searchFilteredPools;
        },

        // Merge claimable CRV from additional rewards (in v3 gauges crv rewards are in extra rewards)
        // with main CRV rewards
        mergeCrvClaimableRewards({ claimableCRV, claimableAdditionalRewards }) {
            // Start with an object that contains all pools ids with either claimable crv or claimable
            // additional rewards as keys, so that when iterating over those keys to sum things up,
            // no pools are omitted.
            const paddedClaimableCRV = {
                ...arrayToHashmap(Array.from(Object.keys(claimableAdditionalRewards)).map((poolId) => [poolId, 0])),
                ...(claimableCRV || {}),
            };

            const augmentedClaimableCRV = arrayToHashmap(Array.from(Object.entries(paddedClaimableCRV)).map(([poolId, amountCrv]) => [
                poolId,
                amountCrv + ((claimableAdditionalRewards[poolId] || []).find(({ name }) => name === 'CRV')?.amount || 0),
            ]));
            const augmentedClaimableAdditionalRewards = arrayToHashmap(Array.from(Object.entries(claimableAdditionalRewards)).map(([poolId, rewards]) => [
                poolId,
                rewards.filter(({ name }) => name !== 'CRV'),
            ]));

            return { augmentedClaimableCRV, augmentedClaimableAdditionalRewards };
        },

        matchSearchText(pool, searchText) {
            const lcSearchText = searchText.trim().toLowerCase();
            const lcName = pool.name.toLowerCase();
            const lcAssets = pool.isFactoryOnlyPool ?
                pool.coins.map(({ symbol }) => symbol).join('+').toLowerCase() :
                pool.assets.toLowerCase();

            return (
                lcName.includes(lcSearchText) ||
                lcAssets.includes(lcSearchText)
            );
        },

        filterByPoolType(poolType) {
            this.poolTypeFilter = poolType;

            if (this.handleViewMyPools) this.handleViewMyPools(poolType === 'mine');
        },

        poolAdditionalRewards(poolId) {
            const pool = getPools().find(({ id }) => poolId === id);

            return pool.additionalRewards.map(({ symbol, tokenPrice, apy }) => ({
                name: symbol,
                rewardsAmount: apy,
                price: tokenPrice,
            }));
        },
    },
}
</script>

<style scoped>
.poolsdialog div:hover {
    background: none;
    color: initial;
}

fieldset legend {
    text-align: center;
}

.table-controls {
    margin-bottom: 10px;
}

.table-controls input {
    width: 140px;
    margin-right: 20px;
}

table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
}

.all-pools-link-container {
    display: block;
    margin: 20px 0px 5px;
    text-align: center;
}

table>>>.sorting-icon {
    position: absolute;
    text-indent: 5px;
}

.empty-td {
    text-align: center;
    font-style: italic;
}

.full-line.loading.line {
    width: 100%;
    text-align: center;
}

.mobile-show {
    display: none;
}

.viewed-address.viewed-address {
    width: 300px;
    float: right;
}

@media only screen and (max-device-width : 900px) {
    .viewed-address.viewed-address {
        display: none;
    }
}

@media only screen and (max-device-width : 730px) {
    .mobile-show {
        display: inline;
    }

    .showPersonalStats .volume {
        display: none;
    }

    .showPersonalStats .tvl {
        background: none;
    }

    td {
        padding-left: 2px;
        padding-right: 2px;
    }

    td:nth-child(even) {
        background: rgba(255, 255, 255, 0.1);
    }
}

.table-controls button {
    box-shadow: 0.2em 0.2em 3px rgb(0 0 0 / 50%) !important;
}

.pressed {
    transform: none !important;
    top: 0 !important;
    left: 0 !important;
    margin-right: 0.0em;
}

.table-controls button:hover {
    transform: translate3d(.1em, .1em, 0.5em);
}

.lh {
    line-height: 1.3em;
    padding: 0.5em 0;
}

.hide-low-tvl-pools {
    padding-bottom: 5px;
    text-align: right;
}
</style>
